// Design tokens for design tools, from the same tokens and themes the CSS uses.
//
// Figma imports W3C design token (DTCG) files as variables, one file per mode,
// and reads colors, px dimensions, durations in seconds and single font family
// names. Penpot reads the Tokens Studio layout: token sets plus $themes and
// $metadata. Both are written here from one list of values.
import { toRgb } from '../content/color.ts'
import { roleNames, type Roles, type ThemePair } from '../content/theme.ts'
import { SYSTEM_NAME, SYSTEM_VERSION, tokens } from '../content/system.ts'

type Token = { $type: string; $value: unknown; $description?: string }
type Group = { [key: string]: Token | Group }

const rem = (value: string) => (value.endsWith('rem') ? parseFloat(value) * 16 : parseFloat(value))
/** The largest size a fluid clamp() reaches, which is what a desktop design shows. */
const largest = (value: string) => {
  const match = /^clamp\(([^,]+),[^,]+,([^)]+)\)$/.exec(value)
  return rem(match ? match[2].trim() : value)
}
const firstFamily = (stack: string) => stack.split(',')[0].trim().replace(/^"|"$/g, '').replace(/ Variable$/, '')
const byGroup = (group: string) => tokens.filter((token) => token.group === group)
const key = (name: string) => name.split('.').slice(1).join('-')

/** A DTCG color: sRGB components from 0 to 1, with the hex for tools that show it. */
const dtcgColor = (hex: string) => {
  const [r, g, b] = toRgb(hex)
  return { colorSpace: 'srgb', components: [r, g, b].map((channel) => Math.round((channel / 255) * 10000) / 10000), alpha: 1, hex }
}

/** Everything that does not change between light and dark. */
function core(): Group {
  const dimension = (value: number): Token['$value'] => ({ value, unit: 'px' })
  return {
    space: Object.fromEntries(byGroup('spacing').map((token) => [key(token.name), { $type: 'dimension', $value: dimension(rem(token.value)), $description: token.role }])),
    radius: Object.fromEntries(byGroup('shape').map((token) => [key(token.name), { $type: 'dimension', $value: dimension(rem(token.value)), $description: token.role }])),
    text: Object.fromEntries(
      byGroup('typography')
        .filter((token) => token.name.startsWith('text.'))
        .map((token) => [key(token.name), { $type: 'dimension', $value: dimension(largest(token.value)), $description: token.role }]),
    ),
    font: Object.fromEntries(
      byGroup('typography')
        .filter((token) => token.name.startsWith('font.'))
        .map((token) => [key(token.name), { $type: 'fontFamily', $value: firstFamily(token.value), $description: token.role }]),
    ),
    duration: Object.fromEntries(
      byGroup('motion')
        .filter((token) => token.name.startsWith('duration.'))
        .map((token) => [key(token.name), { $type: 'duration', $value: { value: parseFloat(token.value) / 1000, unit: 's' }, $description: token.role }]),
    ),
  }
}

const describe = (name: string) => tokens.find((token) => token.css === `--mds-${name}`)?.role

/**
 * One DTCG file per mode, for Figma's variable import: drop light.tokens.json
 * and dark.tokens.json on a new collection and each becomes a mode.
 */
export function dtcgModes(theme: ThemePair, label: string): Record<'light' | 'dark', string> {
  const file = (roles: Roles, mode: string) =>
    `${JSON.stringify(
      {
        $description: `${SYSTEM_NAME} ${SYSTEM_VERSION}: ${label}, ${mode}.`,
        color: Object.fromEntries(
          roleNames.map((name) => [name, { $type: 'color', $value: dtcgColor(roles[name]), $description: describe(name) }]),
        ),
        ...core(),
      },
      null,
      2,
    )}\n`
  return { light: file(theme.light, 'light'), dark: file(theme.dark, 'dark') }
}

/** Penpot's token file: a core set, a light and a dark color set, and a theme for each mode. */
export function penpotTokens(theme: ThemePair, label: string): string {
  const px = (value: number) => `${value}px`
  const colors = (roles: Roles) => Object.fromEntries(roleNames.map((name) => [name, { $type: 'color', $value: roles[name], $description: describe(name) }]))
  const set = (group: string, type: string, values: Array<[string, string]>) => ({
    [group]: Object.fromEntries(values.map(([name, value]) => [name, { $type: type, $value: value }])),
  })
  const coreSet = {
    ...set('space', 'spacing', byGroup('spacing').map((token) => [key(token.name), px(rem(token.value))])),
    ...set('radius', 'borderRadius', byGroup('shape').map((token) => [key(token.name), px(rem(token.value))])),
    ...set(
      'text',
      'fontSizes',
      byGroup('typography')
        .filter((token) => token.name.startsWith('text.'))
        .map((token) => [key(token.name), px(largest(token.value))]),
    ),
    ...set(
      'font',
      'fontFamilies',
      byGroup('typography')
        .filter((token) => token.name.startsWith('font.'))
        .map((token) => [key(token.name), firstFamily(token.value)]),
    ),
  }
  return `${JSON.stringify(
    {
      core: coreSet,
      light: { color: colors(theme.light) },
      dark: { color: colors(theme.dark) },
      $themes: [
        { id: 'light', name: 'Light', group: label, selectedTokenSets: { core: 'enabled', light: 'enabled' } },
        { id: 'dark', name: 'Dark', group: label, selectedTokenSets: { core: 'enabled', dark: 'enabled' } },
      ],
      $metadata: { tokenSetOrder: ['core', 'light', 'dark'] },
    },
    null,
    2,
  )}\n`
}
